import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { externalLinkProps } from '@/lib/externalLinks'
import { figureLabel, kindLabel, stated } from '@/lib/circularTags'
import { whenText } from '@/lib/time'
import { Icon } from '@/icons/Icon'
import { ApiError, get, post } from '@/api/client'
import { HiddenInPublicView, useViewMode } from '@/lib/viewMode'
import { useExpanded } from '@/lib/expandedCircular'
import { renderMarkdown } from '@/lib/markdown'
import { TONE_TINT, TONE_VAR, type CircularDetail } from '@/types/circulars'

import '@/styles/components/circular-tags.css'
import '@/styles/components/assistant.css'


/**
 * The `.dash-circ` expandable analysis card.
 *
 * Rendered identically on the dashboard and inside the circular feed. In the
 * prototype the feed cloned this DOM out of a hidden section with
 * `cloneNode(true)`; here both callers render the same component from the same
 * data, which is what removes the clone-and-rewire machinery entirely.
 */
export function CircularCard({
  circular,
  defaultOpen = false,
}: {
  circular: CircularDetail
  defaultOpen?: boolean
}) {
  // One circular open at a time, across the whole list (lib/expandedCircular).
  const [open, toggle] = useExpanded(circular.id, defaultOpen)
  const a = circular.analysis

  return (
    <div className={open ? 'dash-circ is-open' : 'dash-circ'}>
      <button
        className="dash-circ__head"
        type="button"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="ix-row__meta">
          <span className="ix-row__tags">
            <span className="ix-chip ix-chip--brand ix-chip--tag">{circular.lobCode}</span>
            {/* The feed's own pill where it published one - "INFORMATIONAL" is a
                word the two-member badge enum could not hold, so it used to be
                shown as "Needs review", a chip contradicting the record. The
                editor's badge is next, for a circular written up by hand, and
                where neither exists the chip is simply absent rather than
                derived from a rule the document never stated. */}
            {a?.badgeLabel ? (
              <span className={`ix-badge ix-badge--${a.badge ?? 'review'}`}>
                {a.badgeLabel.charAt(0) + a.badgeLabel.slice(1).toLowerCase()}
              </span>
            ) : (
              a?.badge && (
                <span className={`ix-badge ix-badge--${a.badge}`}>
                  {a.badge === 'mandatory' ? 'Mandatory' : 'Needs review'}
                </span>
              )
            )}
            {/* The status chip, unless it would repeat the badge beside it. Both
                read the record's own pill now, so a feed circular showed
                "MANDATORY · MANDATORY" - the same word twice, saying nothing the
                first one had not. A curated circular still shows both, because
                its status ("Approved") and its badge ("Needs review") are two
                different facts. */}
            {circular.statusLabel &&
              circular.statusLabel.toLowerCase() !== (a?.badgeLabel ?? '').toLowerCase() && (
                <span className={`cf-status cf-status--${circular.statusTone}`}>
                  {circular.statusLabel}
                </span>
              )}
            <span className="cf-tag cf-tag--bur">{circular.bureauLabel}</span>
            {/* What the document calls itself, falling back to the routing
                template's name for a circular with no record behind it. */}
            {/* How Alert routing handles this bulletin - a word, which is what a
                chip is for. What the *document* calls itself is a title in some
                states: California states a 127-character one, which as a chip was
                cut to "Commissioner's Decisi…" and told nobody anything. It reads
                as text above the figure instead. */}
            {(circular.bulletinTypeLabel ?? kindLabel(circular.kind)) && (
              <span
                className={circular.bulletinTypeBinding ? 'cf-tag cf-tag--binding' : 'cf-tag'}
                title="How Alert routing handles this kind of bulletin"
              >
                {circular.bulletinTypeLabel ?? kindLabel(circular.kind)}
              </span>
            )}
            {/* Who published it - but only when it says more than the bureau
                chip a few pixels to its left. That chip reads "CA CDI" and this
                read "Public Record (CA CDI)": the same body named twice, with a
                phrase of our own wrapped round it the second time. */}
            {(a?.sourceAuthority ?? circular.derivedSourceAuthority) !== circular.bureauLabel &&
              (a?.sourceAuthority ?? circular.derivedSourceAuthority) && (
                <span style={{ fontSize: 12, fontWeight: 400, lineHeight: '100%', color: 'var(--ix-text-secondary)' }}>
                  {a?.sourceAuthority ?? circular.derivedSourceAuthority}
                </span>
              )}
          </span>
          {/* How long it has been on the record, counted today and meaning the
              same thing on every card. */}
          <span className="ix-row__deadline">{circular.derivedTiming ?? ''}</span>
        </span>

        {/* Tracking number and priority exist only on an analysed circular.
            Rendered unconditionally they left an empty outlined box. */}
        {(a?.trackingNumber || a?.priority) && (
          <span className="ix-cluster" style={{ gap: 10 }}>
            {a?.trackingNumber && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 6px',
                  borderRadius: 4,
                  boxShadow: 'inset 0 0 0 1px var(--ix-blue-100)',
                  fontSize: 11,
                  fontWeight: 500,
                  lineHeight: '100%',
                  color: 'var(--ix-primary)',
                }}
              >
                {a.trackingNumber}
              </span>
            )}
            {a?.priority && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: '100%',
                  color: a.priority === 'high' ? 'var(--ix-error)' : 'var(--ix-warning)',
                }}
              >
                {a.priority.charAt(0).toUpperCase() + a.priority.slice(1)}
              </span>
            )}
          </span>
        )}

        <span className="ix-cluster" style={{ gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, lineHeight: '100%', color: 'var(--ix-text-primary)' }}>
            {circular.code}
          </span>
          <span style={{ color: 'var(--ix-text-muted)' }}>·</span>
          {/* The line this circular is about. These were the words
              "Workers' Compensation", printed on every card whatever its line -
              true of this carrier's book today and not of the record in front of
              anybody. */}
          {circular.lobName && (
            <span style={{ fontSize: 12, fontWeight: 700, lineHeight: '100%', color: 'var(--ix-primary)' }}>
              {circular.lobName}
            </span>
          )}
          <span style={{ fontSize: 12, fontWeight: 400, lineHeight: '100%', color: 'var(--ix-text-muted)' }}>
            - {circular.stateCode}
          </span>
        </span>

        {/* Both of these are lines, not items in the cluster above. `flexBasis:
            '100%'` looked like it gave them one, but a flex child still shrinks
            to share a line when there is room, so whether the summary got a line
            of its own depended on how long the code and the state happened to be.
            A block always gets one. */}
        {circular.summaryLine && (
          <span className="ix-row__summary">{circular.summaryLine}</span>
        )}
        {(a?.documentType ?? circular.derivedDocumentType) && (
          <span className="ix-row__doctype">{a?.documentType ?? circular.derivedDocumentType}</span>
        )}

        <span className="ix-row__meta">
          <span className="ix-cluster" style={{ gap: 6 }}>
            {/* Nothing where the record states no figure: see `stated`. */}
            {stated(circular.rateChangeDisplay) && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: '100%',
                  color: TONE_VAR[circular.rateTone] ?? 'var(--ix-primary)',
                }}
              >
                {circular.rateChangeDisplay}
              </span>
            )}
            {/* Say what the number is, in the feed's own words where there are
                any - see `figureLabel`. A record that names it nothing gets no
                line, rather than "vs prior approved", which nothing published. */}
            {stated(circular.rateChangeDisplay) && figureLabel(circular) && (
              <span style={{ fontSize: 11, fontWeight: 400, lineHeight: '100%', color: 'var(--ix-text-secondary)' }}>
                {figureLabel(circular)}
              </span>
            )}
            {/* The separator belongs to the figure, so it goes when the figure
                does - otherwise a circular with no rate change opened its line
                with a stray dot. */}
            {stated(circular.rateChangeDisplay) && (
              <span style={{ color: 'var(--ix-text-muted)' }}>·</span>
            )}
            <span style={{ fontSize: 11, fontWeight: 500, lineHeight: '100%', color: 'var(--ix-warning)' }}>
              Effective Date: {circular.effectiveDisplay.replace(/^Eff\.\s*/, '')}
            </span>
          </span>
          <Icon
            name="chevron-down"
            size={14}
            style={{
              color: 'var(--ix-text-muted)',
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : undefined,
            }}
          />
        </span>
      </button>

      {open && <CircularPanel circular={circular} />}
    </div>
  )
}

export function CircularPanel({ circular }: { circular: CircularDetail }) {
  const a = circular.analysis
  return (
    <div className="dash-circ__panel" style={{ display: 'flex' }}>
      <div className="dash-facts">
        {/* Effective date stays whatever it says - a change with no date set is
            worth seeing as that, and a reader scanning for "when does this bite"
            must not mistake a hidden row for one they missed. The two below are
            left out when the feed carries neither: a panel of dashes fills the
            space somebody is scanning with items that say only that there is
            nothing to say. */}
        <Fact k="Effective date" v={circular.effectiveDisplay.replace(/^Eff\.\s*/, '')} />
        <Fact k="Published" v={circular.publishedAt} />
        {a?.indicationDisplay && (
          <Fact k="Indication / filed" v={a.indicationDisplay} tone={circular.rateTone} />
        )}
        {a?.exposureBase && <Fact k="Exposure base" v={a.exposureBase} />}
      </div>

      <SourceDocuments circular={circular} />

      {/* Every circular gets a summary: the hand-written one where it exists,
          otherwise one composed from the record and this carrier's exposure. */}
      {(a?.executiveSummary || circular.derivedSummary) && (
        <div className="dash-summary">
          <div className="dash-summary__k">
            <Icon name="line-chart" size={13} />
            <span>Executive summary</span>
          </div>
          <p className="dash-summary__p">{a?.executiveSummary || circular.derivedSummary}</p>
        </div>
      )}

      {/* Every circular gets the tabs it has something to fill. `hasTabs` used to
          gate this, and only the one hand-written California filing set it. */}
      <AnalysisTabs circular={circular} />

      {circular.deliveryTags.length > 0 && (
        <div className="dash-tags">
          {circular.deliveryTags.map((t) => (
            <span
              className="dash-tag"
              key={t.id}
              style={{ background: TONE_TINT[t.tone] ?? 'var(--ix-slate-100)', color: TONE_VAR[t.tone] }}
            >
              <Icon name={t.icon} size={12} />
              {t.text}
            </span>
          ))}
        </div>
      )}

      <AskAssistant circularId={circular.id} />
      <ActionBar circular={circular} />
      <FeedbackBar circularId={circular.id} />
    </div>
  )
}

/**
 * The public record behind a circular. Where no documents were attached, the
 * circular's own public-record URL is the document, rather than no link at all.
 */
function SourceDocuments({ circular }: { circular: CircularDetail }) {
  const docs = circular.sourceDocs.length
    ? circular.sourceDocs.map((d) => ({ id: d.id, url: d.url, title: d.title, label: d.recordLabel }))
    : circular.sourceUrl
      ? [{ id: 'source-url', url: circular.sourceUrl, title: `${circular.code} — public record`, label: 'Public record' }]
      : []

  if (!docs.length) return null

  return (
    <div className="ix-stack" style={{ gap: 10 }}>
      <div className="dash-sec-title">
        <Icon name="file-text" size={16} style={{ color: 'var(--ix-text-secondary)' }} />
        <span>Source Documents</span>
      </div>
      {docs.map((doc) => (
        <a className="dash-doc" key={doc.id} href={doc.url} {...externalLinkProps(doc.url)}>
          <span className="ix-chip ix-chip--success ix-chip--tag">{doc.label}</span>
          <span className="dash-doc__text">
            <span className="dash-doc__title">{doc.title}</span>
            <span className="dash-doc__url">{doc.url}</span>
          </span>
          <Icon name="arrow-up-from-line" size={14} style={{ color: 'var(--ix-primary)', transform: 'rotate(45deg)' }} />
        </a>
      ))}
    </div>
  )
}

function Fact({ k, v, tone }: { k: string; v: string; tone?: string }) {
  return (
    <div className="dash-fact">
      <span className="dash-fact__k">{k}</span>
      <span className="dash-fact__v" style={tone ? { color: TONE_VAR[tone] } : undefined}>
        {v}
      </span>
    </div>
  )
}

/**
 * The tabs one circular has something to fill.
 *
 * The prototype showed all six on the single filing it hand-wrote. Impact is
 * always here, because it is derived from the carrier's own portfolio and can
 * explain itself when there is no portfolio yet; the rest appear only when the
 * filing carries them, so no tab opens onto an empty panel.
 */
function AnalysisTabs({ circular }: { circular: CircularDetail }) {
  const [tab, setTab] = useState(0)
  const { isPublic } = useViewMode()
  const a = circular.analysis

  const tabs: { label: string; body: ReactNode }[] = [
    { label: 'Impact', body: <ImpactPanel circular={circular} /> },
    {
      label: 'Class & Territory',
      // Class tables are bureau content the carrier holds under licence. Public
      // view keeps the tab and says where to see it, rather than hiding that it exists.
      body: isPublic ? (
        <HiddenInPublicView what="Classification detail (bureau content held under licence)" />
      ) : circular.classTable ? (
        // The record's own classification list. Its columns are the keys the
        // record's objects carry, so this renders whatever the feed publishes -
        // and it was arriving as one long line in Filing & Compliance,
        // "2003 · Editorial, 2030 · Eliminated, …", which is a table written as
        // a sentence.
        <>
          <div className="dash-note">
            Classifications this change applies to, as the filing lists them
            {circular.classTable.rows.length > 1 ? ` — ${circular.classTable.rows.length} of them` : ''}.
          </div>
          {/* Capped and scrolled: a filing can list dozens - California's runs to
              77 - and an expanded card that tall buries the next circular. The
              header stays put while the rows move. */}
          <div className="dash-scroll">
          <table className="dash-table dash-table--record">
            <thead>
              <tr>
                {circular.classTable.columns.map((head) => (
                  <th key={head}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {circular.classTable.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, n) => (
                    <td key={n}>
                      {n === 0 ? <span className="dash-table__code">{cell}</span> : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      ) : circular.classRows.length > 0 ? (
        <>
          <div className="dash-note">Classifications the approved change applies to.</div>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Classification</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {circular.classRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.classCode}</td>
                  <td>{r.classification}</td>
                  <td>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ix-primary)' }}>{r.change}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <div className="dash-note">Classification detail is not published with this filing.</div>
      ),
    },
    // Forms, only where there are some.
    //
    // This tab was always present and, with nothing behind it, said "No form
    // changes in this filing." - a statement about the filing that this
    // application has no way to make. Nothing extracts form changes: the feed
    // sends no field about them, and `circular_forms` is written by the seed
    // alone, so on any real installation the sentence appeared on every
    // circular and meant only "we have no form data". An empty tab on every
    // card is also a promise of content that never arrives.
    //
    // The neighbouring "Classification detail is not published with this
    // filing" is a different case and stays: CLASS_CODES is a field the feed
    // does send, so its absence really is a fact about the filing.
    ...(circular.forms.length > 0
      ? [
          {
            label: 'Forms',
            body: isPublic ? (
              <HiddenInPublicView what="Form changes (bureau content held under licence)" />
            ) : (
              <div className="dash-formlist">
                {circular.forms.map((f) => (
                  <div className="dash-formrow" key={f.id}>
                    <span className="dash-formrow__code">{f.code}</span>
                    <span className="dash-formrow__name">{f.name}</span>
                    <span className="dash-formrow__note">{f.note}</span>
                  </div>
                ))}
              </div>
            ),
          },
        ]
      : []),
    { label: 'Filing & Compliance', body: <FilingPanel circular={circular} /> },
    { label: 'Implementation', body: <ImplementationPanel circular={circular} /> },
    {
      label: 'Context',
      body: <p className="dash-context">{a?.context || circular.note}</p>,
    },
  ]

  const current = tabs[Math.min(tab, tabs.length - 1)]

  return (
    <div className="dash-tabgroup">
      <div className="dash-tabs" role="tablist">
        {tabs.map((t, i) => (
          <button
            key={t.label}
            className={i === Math.min(tab, tabs.length - 1) ? 'dash-tab is-active' : 'dash-tab'}
            type="button"
            role="tab"
            aria-selected={i === Math.min(tab, tabs.length - 1)}
            onClick={() => setTab(i)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="dash-tabpanel is-active" role="tabpanel">
        {current.body}
      </div>
    </div>
  )
}

/**
 * Premium, policies and annual impact for THIS carrier, computed by the API
 * from the portfolio it holds - not the prototype's one hand-written block.
 * With no portfolio it says how to get one rather than showing $0.
 */
function ImpactPanel({ circular }: { circular: CircularDetail }) {
  const impact = circular.derivedImpact

  // Impact stays visible in Public view: it is the point of the page, and it is
  // the carrier's own figure, not bureau-licensed content.
  return (
    <>
      {impact?.available ? (
        <>
          <div className="dash-stats">
            {impact.stats.map((s) => (
              <div className="dash-stat" key={s.key}>
                <span className="dash-stat__k">{s.key}</span>
                <span className="dash-stat__v" style={{ color: TONE_VAR[s.tone] ?? 'var(--ix-text-primary)' }}>
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="dash-empty-impact">
          <Icon name="file-text" size={16} style={{ color: 'var(--ix-text-muted)' }} />
          <div>
            <div className="dash-empty-impact__t">Impact not calculated yet</div>
            <p className="dash-empty-impact__b">
              {impact?.message ?? 'No portfolio data yet.'}
            </p>
            <Link className="ix-btn ix-btn--compact" to="/onboarding/source">
              Add your portfolio data
            </Link>
          </div>
        </div>
      )}

      {circular.trendRows.length > 0 && <TrendRows circular={circular} />}
    </>
  )
}

/**
 * Implementation is the response board's job, so this tab hands over to it
 * rather than keeping a second, read-only copy of the plan here.
 */
function ImplementationPanel({ circular }: { circular: CircularDetail }) {
  const viewer = useViewerState()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const boardId = viewer.data?.items?.[circular.id]?.changeResponseId ?? null

  // Open *this* circular's board, making one if it has none.
  //
  // This was a link to `/change-response` whenever `boardId` was null - the
  // board list - so every circular without a board sent you to the same page,
  // which reads as the link being wrong rather than as there being nothing to
  // open. The endpoint returns the existing board or opens one, so there is
  // never a second board for a circular and the button always lands on the
  // right one. It is the same call `Create ticket` makes.
  const open = useMutation({
    mutationFn: () => post<{ changeId: string; created: boolean }>(`/circulars/${circular.id}/change-response`),
    onSuccess: ({ changeId }) => {
      void qc.invalidateQueries({ queryKey: VIEWER_STATE_KEY })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(`/change-response/${changeId}`)
    },
  })

  return (
    <>
      {circular.steps.length > 0 && (
        <div className="dash-steps">
          {circular.steps.map((s) => (
            <div className="dash-step" key={s.id}>
              <span className="dash-step__n">{s.ordinal + 1}</span>
              <span className="dash-step__t">{s.task}</span>
              <span className="dash-step__owner">{s.ownerDepartment}</span>
            </div>
          ))}
        </div>
      )}

      <div className="dash-empty-impact">
        <Icon name="git-pull-request" size={16} style={{ color: 'var(--ix-text-muted)' }} />
        <div>
          <div className="dash-empty-impact__t">
            {boardId ? 'This bulletin has a response board' : 'The work is planned on a response board'}
          </div>
          <p className="dash-empty-impact__b">
            {boardId
              ? 'Who owns each step, what it waits on and what has been signed off all live there.'
              : 'Opening one starts it off with Alert routing’s plan for this bulletin type.'}
          </p>
          <button
            type="button"
            className="ix-btn ix-btn--compact"
            disabled={open.isPending}
            onClick={() => {
              if (boardId) navigate(`/change-response/${boardId}`)
              else open.mutate()
            }}
          >
            {open.isPending
              ? 'Opening…'
              : boardId
                ? 'Open the response board'
                : 'Open a response board for this bulletin'}
          </button>
          {open.isError && (
            <p className="dash-empty-impact__b" role="alert">
              The response board could not be opened. Please try again.
            </p>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Filing facts. A circular that was written up by hand has a deadline, a SERFF
 * number and the requirement; every other circular still has the dates, status
 * and who filed it, which the API derives from the record.
 */
function FilingPanel({ circular }: { circular: CircularDetail }) {
  const a = circular.analysis

  // Both, not one or the other. This used to show the four written facts *or*
  // the derived ones, so a circular that happened to state a filing deadline
  // lost its published date, its status, who filed it and every other field its
  // own record carried.
  const written = [
    { k: 'Filing deadline', v: a?.filingDeadline, tone: 'error' },
    { k: 'SERFF tracking', v: a?.serffTracking, tone: 'primary' },
    { k: 'Requirement', v: a?.requirement, tone: 'primary' },
    { k: 'State tracking', v: a?.stateTracking, tone: 'primary' },
  ].filter((cell) => cell.v)
  const seen = new Set(written.map((cell) => cell.k.toLowerCase()))

  return (
    <>
      <div className="dash-fc">
        {written.map((cell) => (
          <FcCell key={cell.k} k={cell.k} v={cell.v ?? null} tone={cell.tone} />
        ))}
        {circular.derivedFiling
          .filter((cell) => !seen.has(cell.key.toLowerCase()))
          .map((cell) => (
            <FcCell key={cell.key} k={cell.key} v={cell.value} tone={cell.tone} />
          ))}
      </div>
      {a?.complianceBanner && <div className="dash-fc__banner">{a.complianceBanner}</div>}
    </>
  )
}

/** The regulator's own decomposition of the change: public, either way. */
function TrendRows({ circular }: { circular: CircularDetail }) {
  return (
    <div className="dash-trend">
      <div className="dash-trend__title">Trend Decomposition</div>
      {circular.trendRows.map((r) => (
        <div className="dash-trend__row" key={r.id}>
          <span>{r.label}</span>
          {r.tone === 'plain' ? (
            <span style={{ fontSize: 13, fontWeight: 700 }}>{r.value}</span>
          ) : (
            <span className="dash-trend__pill" style={{ background: TONE_TINT[r.tone], color: TONE_VAR[r.tone] }}>
              {r.value}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

function FcCell({ k, v, tone }: { k: string; v: string | null; tone: string }) {
  if (!v) return null
  return (
    <div className="dash-fc__cell" style={{ background: TONE_TINT[tone] }}>
      <span className="dash-fc__k">{k}</span>
      <span className="dash-fc__v" style={{ color: TONE_VAR[tone] }}>
        {v}
      </span>
    </div>
  )
}

/** The thread without the question still in flight - the server's own copy of
    it comes back with the answer, so keeping both would show it twice. */
function settled(prev: Conversation | undefined): AssistantMessage[] {
  return (prev?.messages ?? []).filter((m) => !m.pending)
}

/**
 * How long to wait for an answer after a question before handing it back to the
 * person.
 *
 * Short on purpose. The model takes as long as it takes and some questions are
 * never answered at all, so a long automatic wait is a spinner that outlives
 * anybody's patience while quietly calling the service every few seconds. After
 * this, the thread says the answer has not arrived and offers Refresh - which
 * reads the conversation once, on a click, which is what the person wanted.
 */
const ANSWER_WAIT_MS = 30_000

/**
 * The thread, kept in this browser.
 *
 * The conversation belongs to the assistant service and Insuronix stores none
 * of it, so reopening a card meant fetching it again - and each fetch is a
 * round trip to that service. Keeping the last view here means a card opens on
 * what it said last time, immediately, and the network is only used to catch up.
 *
 * Per browser and per person's device by nature: it is this viewer's own copy
 * of their own conversation, it never leaves the machine, and losing it costs
 * one fetch. Every read and write is guarded because storage can be absent,
 * full, or throw outright in a private window.
 */
const CACHE_PREFIX = 'ix.assistant.'

function readCached(circularId: string): AssistantMessage[] | null {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + circularId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as AssistantMessage[]) : null
  } catch {
    return null
  }
}

function writeCached(circularId: string, messages: AssistantMessage[]) {
  try {
    window.localStorage.setItem(CACHE_PREFIX + circularId, JSON.stringify(messages))
  } catch {
    // A full or blocked store is not worth failing a conversation over.
  }
}

const ASSISTANT_FAILED = 'The assistant could not answer that just now. Please try again.'

/**
 * What a failed question says.
 *
 * One sentence for everything except a validation error, which is about what
 * the person typed and is theirs to act on. Passing the raw message through
 * looked right - the assistant's own failure already answers with this exact
 * sentence - but any *other* failure carries whatever the transport said, and
 * the panel duly printed "Not Found" at somebody. Nothing about the service
 * behind this belongs in front of the person asking.
 */
function errorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 422 && err.message) return err.message
  return ASSISTANT_FAILED
}

type AssistantMessage = {
  role: 'user' | 'assistant'
  body: string
  failed?: boolean
  /** On screen while the answer is awaited, then replaced by what came back. */
  pending?: boolean
  /** The question this failure was the answer to, so it can be asked again. */
  retryOf?: string
}
type Conversation = {
  available: boolean
  messages: AssistantMessage[]
  /** The last question has no answer yet, so keep re-reading. */
  waiting: boolean
  notice: string | null
}

/**
 * Ask Portfolio Monitoring Assistant.
 *
 * The conversation belongs to this person and this circular, and it lives on
 * the assistant service - the browser never talks to it. Every call goes to
 * Insuronix's own API, which makes the outbound request, so nothing here knows
 * a third-party address and nothing appears in the network tab but our own
 * endpoints.
 *
 * The thread is read when the card is opened and never before - this component
 * only mounts inside an expanded panel - so scrolling a feed does not touch a
 * conversation for every circular that went past.
 */
function AskAssistant({ circularId }: { circularId: string }) {
  const qc = useQueryClient()
  const [question, setQuestion] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  // The question just asked, held here until the service's own copy of the
  // thread shows it. The poll overwrites the thread with what the service says,
  // and the service does not list a question the moment it accepts one - so a
  // question put in the thread optimistically vanished on the first poll,
  // leaving a "Thinking…" with nothing above it.
  const [pending, setPending] = useState<string | null>(null)
  // When a question was asked from this card, if one was. Polling hangs off
  // this and nothing else - see below.
  const askedAt = useRef<number | null>(null)
  const key = ['assistant', circularId]

  const convo = useQuery({
    queryKey: key,
    queryFn: async () => {
      const fresh = await get<Conversation>(`/assistant/circulars/${circularId}`)
      writeCached(circularId, fresh.messages)
      return fresh
    },
    // What this browser saw last time, so the card opens on the conversation
    // instead of on nothing while a round trip happens.
    placeholderData: () => {
      const cached = readCached(circularId)
      return cached ? { available: true, messages: cached, waiting: false, notice: null } : undefined
    },
    staleTime: 30_000,
    retry: false,
    // Poll only for an answer to a question asked from this card, and only for
    // as long as one could plausibly arrive.
    //
    // Polling on the server's `waiting` alone looked right and was wrong: a
    // question can stay unanswered for ever - there is one on the record that
    // never came back - and any thread ending in one made every card that
    // showed it call the service every few seconds, on every open, for an
    // answer that was never coming. Nobody asked anything; the card just
    // arrived at an old conversation.
    refetchInterval: (query) => {
      if (askedAt.current === null) return false
      if (Date.now() - askedAt.current >= ANSWER_WAIT_MS) return false
      return (query.state.data as Conversation | undefined)?.waiting ? 4_000 : false
    },
  })

  const ask = useMutation({
    mutationFn: (q: string) =>
      post<{ messages: AssistantMessage[] }>(`/assistant/circulars/${circularId}/ask`, {
        question: q,
      }),
    // The question goes up the moment it is asked. Waiting for the answer to
    // render it meant pressing Ask cleared the box and left nothing behind but
    // "Thinking…" - the person could no longer see what they had asked.
    onMutate: (q) => {
      askedAt.current = Date.now()
      setPending(q)
    },
    onSuccess: () => {
      setProblem(null)
      // Nothing is written into the thread here: the answer is not in the
      // reply and cannot be, and the question is held in `pending` until the
      // service lists it itself.
      void convo.refetch()
    },
    // A failed question is part of the conversation, not a banner beside it:
    // the question stays on screen and the answer says it did not work, which
    // is how a chat reads. Whatever went wrong, it says the same thing - the
    // detail is in the assistant call history, which an administrator reads.
    onError: (e, q) => {
      setProblem(null)
      setPending(null)
      qc.setQueryData<Conversation>(key, (prev) => ({
        available: true,
        notice: null,
        waiting: false,
        messages: [
          ...settled(prev),
          { role: 'user', body: q },
          { role: 'assistant', body: errorText(e), failed: true, retryOf: q },
        ],
      }))
    },
  })

  /** Ask the same question again, dropping the attempt that failed.
   *
   * Nothing is retried automatically: the failure is usually something only a
   * person can judge - a service that is briefly down, a quota that resets -
   * and a card that quietly re-asks would multiply one bad minute across every
   * open circular. */
  const retry = (index: number, q: string) => {
    qc.setQueryData<Conversation>(key, (prev) => ({
      available: true,
      notice: null,
      waiting: false,
      messages: (prev?.messages ?? []).filter((_, i) => i !== index && i !== index - 1),
    }))
    ask.mutate(q)
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const q = question.trim()
    if (!q || ask.isPending) return
    setQuestion('')
    ask.mutate(q)
  }

  const served = convo.data?.messages ?? []
  // Once the service lists the question itself, stop showing our own copy of it.
  const listed = pending !== null && served.some((m) => m.role === 'user' && m.body === pending)
  useEffect(() => {
    if (listed) setPending(null)
  }, [listed])
  const thread: AssistantMessage[] =
    listed || pending === null ? served : [...served, { role: 'user', body: pending }]
  const unavailable = convo.data?.available === false
  // Still coming: either the ask is in flight, or it landed and the answer has
  // not appeared in the thread yet and we are still within the wait.
  const gaveUp =
    askedAt.current !== null && Date.now() - askedAt.current >= ANSWER_WAIT_MS
  const awaitingAnswer =
    ask.isPending ||
    (askedAt.current !== null && !gaveUp && (pending !== null || convo.data?.waiting === true))
  // The question went in and nothing came back. Said plainly, because the
  // alternative is a "Thinking…" that never stops.
  const answerNeverCame = convo.data?.waiting === true && gaveUp && !ask.isPending

  return (
    <div className="dash-ask">
      <div className="dash-sec-title">
        <Icon name="sparkles" size={14} style={{ color: 'var(--ix-primary)' }} />
        <span>Ask Portfolio Monitoring Assistant</span>
      </div>

      {convo.isLoading && <p className="dash-ask__note">Loading the conversation…</p>}

      {thread.map((m, i) =>
        m.role === 'user' ? (
          <div className="dash-msg-user" key={i}>
            <div className="dash-bubble-user">{m.body}</div>
            <span className="ix-icon-tile ix-icon-tile--sm">
              <Icon name="user" size={13} />
            </span>
          </div>
        ) : (
          <div className="dash-msg-ai" key={i}>
            <span
              className={
                m.failed
                  ? 'ix-icon-tile ix-icon-tile--sm'
                  : 'ix-icon-tile ix-icon-tile--brand ix-icon-tile--sm'
              }
            >
              <Icon name={m.failed ? 'alert-triangle' : 'sparkles'} size={13} />
            </span>
            <div className={m.failed ? 'dash-bubble-ai dash-bubble-ai--failed' : 'dash-bubble-ai'}>
              {/* The assistant writes Markdown - bold figures, bullets, and a
                  table when it lists records. Rendered as elements, never as
                  HTML: the text comes from outside (lib/markdown.tsx). A
                  failure is our own one-line sentence, so it stays plain. */}
              {m.failed ? m.body : renderMarkdown(m.body)}
              {m.failed && m.retryOf && (
                <button
                  type="button"
                  className="dash-ask__retry"
                  disabled={ask.isPending}
                  onClick={() => retry(i, m.retryOf as string)}
                >
                  <Icon name="refresh-cw" size={11} /> Retry
                </button>
              )}
            </div>
          </div>
        ),
      )}

      {awaitingAnswer && (
        <div className="dash-msg-ai">
          <span className="ix-icon-tile ix-icon-tile--brand ix-icon-tile--sm">
            <Icon name="sparkles" size={13} />
          </span>
          <div className="dash-bubble-ai dash-bubble-ai--thinking">Thinking…</div>
        </div>
      )}

      {answerNeverCame && (
        <div className="dash-msg-ai">
          <span className="ix-icon-tile ix-icon-tile--sm">
            <Icon name="clock" size={13} />
          </span>
          <div className="dash-bubble-ai dash-bubble-ai--failed">
            No answer yet. It may still be coming — refresh to look again.
            <button
              type="button"
              className="dash-ask__retry"
              disabled={convo.isFetching}
              onClick={() => void convo.refetch()}
            >
              <Icon name="refresh-cw" size={11} /> Refresh
            </button>
          </div>
        </div>
      )}

      {(problem || unavailable) && (
        <p className="dash-ask__problem" role="alert">
          {problem ?? convo.data?.notice}
        </p>
      )}

      <form className="dash-ask-form" onSubmit={onSubmit}>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask a follow-up question..."
          disabled={unavailable}
        />
        <button
          className="ix-btn ix-btn--compact"
          type="submit"
          disabled={awaitingAnswer || unavailable}
        >
          {awaitingAnswer ? 'Asking…' : 'Ask →'}
        </button>
      </form>
    </div>
  )
}


/**
 * What this user has already done with each circular, in one request for the
 * whole page: the actions they took, the relevance they gave, and the change
 * response opened from it. Without this the buttons forgot everything on reload.
 */
export interface CircularViewerState {
  actions: Record<string, string>
  relevant: boolean | null
  changeResponseId: string | null
}

export const VIEWER_STATE_KEY = ['circulars', 'viewer-state']

export function useViewerState() {
  return useQuery({
    queryKey: VIEWER_STATE_KEY,
    queryFn: () => get<{ items: Record<string, CircularViewerState> }>('/circulars/viewer-state'),
    staleTime: 30_000,
  })
}

function whenLabel(iso: string): string {
  return iso ? whenText(iso) : ''
}

/**
 * Share, Create ticket, Acknowledge and Defer - each doing the thing it says.
 * Share copies a link that opens this circular in the feed; Create ticket opens
 * (or reopens) the change response for it; Acknowledge and Defer are recorded
 * and shown on the card from then on.
 */
function ActionBar({ circular }: { circular: CircularDetail }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const viewer = useViewerState()
  const state = viewer.data?.items?.[circular.id]
  const [notice, setNotice] = useState<string | null>(null)

  const flash = (text: string) => {
    setNotice(text)
    window.setTimeout(() => setNotice(null), 2500)
  }
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: VIEWER_STATE_KEY })
    void qc.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const act = useMutation({
    mutationFn: (action: string) => post(`/circulars/${circular.id}/actions`, { action }),
    onSuccess: (_d, action) => {
      refresh()
      flash(action === 'acknowledge' ? 'Acknowledged.' : 'Deferred - it stays on the record.')
    },
  })

  const share = useMutation({
    mutationFn: async () => {
      const link = `${window.location.origin}/circular-feed?focus=${encodeURIComponent(circular.code)}`
      const copied = await copyLink(link)
      await post(`/circulars/${circular.id}/actions`, { action: 'share' })
      return { link, copied }
    },
    onSuccess: ({ link, copied }) => {
      refresh()
      flash(copied ? 'Link copied to the clipboard.' : `Copy this link: ${link}`)
    },
  })

  const ticket = useMutation({
    mutationFn: () => post<{ changeId: string; created: boolean }>(`/circulars/${circular.id}/change-response`),
    onSuccess: ({ changeId }) => {
      refresh()
      navigate(`/change-response/${changeId}`)
    },
  })

  const busy = act.isPending || share.isPending || ticket.isPending
  const acknowledged = state?.actions?.acknowledge
  const deferred = state?.actions?.defer
  const failure = act.error ?? share.error ?? ticket.error

  return (
    <div className="ix-stack" style={{ gap: 8 }}>
      <div className="dash-actions">
        <button className="ix-btn ix-btn--secondary" type="button" disabled={busy} onClick={() => share.mutate()}>
          <Icon name="link" size={13} />
          {share.isPending ? 'Copying…' : 'Share'}
        </button>
        <button className="ix-btn ix-btn--secondary" type="button" disabled={busy} onClick={() => ticket.mutate()}>
          <Icon name={state?.changeResponseId ? 'arrow-right' : 'plus'} size={13} />
          {ticket.isPending ? 'Opening…' : state?.changeResponseId ? 'Open ticket' : 'Create Ticket'}
        </button>
        <button
          className="ix-btn ix-btn--primary"
          type="button"
          disabled={busy || !!acknowledged}
          onClick={() => act.mutate('acknowledge')}
        >
          <Icon name="check" size={13} />
          {acknowledged ? 'Acknowledged' : 'Acknowledge'}
        </button>
        <button className="ix-btn ix-btn--secondary" type="button" disabled={busy} onClick={() => act.mutate('defer')}>
          <Icon name="clock" size={13} />
          {deferred ? 'Deferred' : 'Defer'}
        </button>
      </div>

      {(acknowledged || deferred || state?.changeResponseId || notice || failure) && (
        <div className="dash-tags">
          {acknowledged && (
            <span className="dash-tag" style={{ background: 'var(--ix-success-tint)', color: 'var(--ix-success)' }}>
              <Icon name="check" size={12} /> Acknowledged by you · {whenLabel(acknowledged)}
            </span>
          )}
          {deferred && !acknowledged && (
            <span className="dash-tag" style={{ background: 'var(--ix-warning-tint)', color: 'var(--ix-warning)' }}>
              <Icon name="clock" size={12} /> Deferred · {whenLabel(deferred)}
            </span>
          )}
          {state?.changeResponseId && (
            <Link
              className="dash-tag"
              to={`/change-response/${state.changeResponseId}`}
              style={{ background: 'var(--ix-info-tint)', color: 'var(--ix-info)', textDecoration: 'none' }}
            >
              <Icon name="git-pull-request" size={12} /> Ticket open
            </Link>
          )}
          {notice && <span className="dash-tag">{notice}</span>}
          {failure && (
            <span className="dash-tag" style={{ background: 'var(--ix-error-tint)', color: 'var(--ix-error)' }}>
              {failure instanceof Error ? failure.message : 'That did not work.'}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/** Clipboard access needs a secure context and permission; say so rather than failing silently. */
async function copyLink(link: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(link)
    return true
  } catch {
    return false
  }
}

function FeedbackBar({ circularId }: { circularId: string }) {
  const qc = useQueryClient()
  const viewer = useViewerState()
  const saved = viewer.data?.items?.[circularId]?.relevant ?? null
  const [choice, setChoice] = useState<boolean | null>(null)
  const current = choice ?? saved
  const send = useMutation({
    mutationFn: (relevant: boolean) => post(`/circulars/${circularId}/feedback`, { relevant }),
    onSuccess: (_d, relevant) => {
      setChoice(relevant)
      void qc.invalidateQueries({ queryKey: VIEWER_STATE_KEY })
    },
  })

  return (
    <div className="dash-feedback">
      {/* Once answered the prompt states the saved answer rather than staying a
          question - a persistent "Was this relevant?" reads as if nothing was
          recorded. The buttons remain so the answer can still be changed. */}
      <span>
        {current === null
          ? 'Was this circular relevant to your book?'
          : current
            ? 'You marked this relevant to your book.'
            : 'You marked this not relevant to your book.'}
      </span>
      {/* Keep the two buttons together at the right rather than letting the
          container's space-between fling them apart with the prompt. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
        {current !== null && <span className="dash-note">Your answer trains the relevance figure.</span>}
        <button
          className="dash-tag"
          type="button"
          aria-pressed={current === true}
          style={{
            border: current === true ? '1px solid var(--ix-success)' : 0,
            cursor: 'pointer',
            background: 'var(--ix-success-tint)',
            color: 'var(--ix-success)',
            opacity: current === false ? 0.45 : 1,
          }}
          onClick={() => send.mutate(true)}
        >
          {current === true && <Icon name="check" size={12} />}
          Relevant
        </button>
        <button
          className="dash-tag"
          type="button"
          aria-pressed={current === false}
          style={{
            border: current === false ? '1px solid var(--ix-error)' : 0,
            cursor: 'pointer',
            background: 'var(--ix-error-tint)',
            color: 'var(--ix-error)',
            opacity: current === true ? 0.45 : 1,
          }}
          onClick={() => send.mutate(false)}
        >
          {current === false && <Icon name="check" size={12} />}
          Not relevant
        </button>
      </div>
    </div>
  )
}

export default CircularCard
